/// <amd-module name="Wiki/PageTree/Components/WikiTree" />
import * as React from "react";
import { getId } from "VSS/Core/Util/Object";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { FilterBar } from "VSSUI/FilterBar";
import { Spinner } from "VSSUI/Spinner";
import { KeywordFilterBarItem } from "VSSUI/TextFilterBarItem";
import { ITreeColumn } from "VSSUI/TreeEx";
import { Filter, FILTER_CHANGE_EVENT, IFilterState } from "VSSUI/Utilities/Filter";
import { ITreeItemEx } from "VSSUI/Utilities/TreeItemProvider";
import { WikiPage } from "Wiki/Clients/Wiki/Contracts/Wiki";
import { WikiPagePathTree, WikiTreeDragDropProps } from "Wiki/PageTree/Components/WikiPagePathTree";
import { FilteredWikiPage, WikiTreePage } from "Wiki/PageTree/Contracts";
import * as Resources from "Wiki/PageTree/Resources";
import { WikiPageTreeItemProvider } from "Wiki/PageTree/WikiPageTreeItemProvider";

// exporting to minimize refactoring changes
export { WikiTreeDragDropProps, RootId } from "Wiki/PageTree/Components/WikiPagePathTree";

export interface IWikiTreeProps {
    additionalColumns?: ITreeColumn<WikiTreePage | FilteredWikiPage>[];

    itemProvider: WikiPageTreeItemProvider;
    filterTreeItemProvider: WikiPageTreeItemProvider; // will be used only when filtering is in progress
    onPageSelected: (newPage: WikiPage) => void;
    onPageToggled: (page: WikiPage, isExpanded: boolean) => void;
    homePagePath: string | undefined;
    onFilteredPageSelected: (newPage: WikiPage) => void;
    onFilterChanged: (filterText: string) => void;
    onFilteringComplete: () => void;
    isFilterLoading: boolean;
    isFiltering: boolean;
    isReorderInProgress?: boolean;
    selectedPagePath?: string;
    openInNewTab?: (path: string) => void;
    wikiTreeDragDropProps?: WikiTreeDragDropProps;

    isHandleBrokenLinksEnabled?: boolean;
    isIncludeEditingAllowed: boolean;
    getPageIsDisabled?: (page: WikiPage) => boolean;
    setInitialFocusToFilter?: boolean;
    wikiName: string; // Used as name of tree node. Tree node corresponding for moving page at root level.
    isShowWikiName?: boolean;
}

export class WikiTree extends VssComponent<IWikiTreeProps> {
    private _filter: Filter;
    private _filterBarRef = React.createRef<FilterBar>();
    private _filterKey = "wikiFilter-" + getId();

    constructor(props: IWikiTreeProps, context: IVssComponentContext) {
        super(props, context);
        this._filter = new Filter();
    }

    public componentDidMount(): void {
        super.componentDidMount();
        if (this._filterBarRef.current && this.props.setInitialFocusToFilter) {
            this._filterBarRef.current.focus();
        }
        this._filter.subscribe(this._onTreeFilterChanged, FILTER_CHANGE_EVENT);
    }

    public componentWillUnmount(): void {
        this._filter.unsubscribe(this._onTreeFilterChanged, FILTER_CHANGE_EVENT);
        super.componentWillUnmount();
    }

    public render(): JSX.Element {
        const showTreeOverlay = this.props.isReorderInProgress;

        return (
            <div className="wiki-tree-pane relative flex-column flex-grow">
                <div className="absolute-fill flex-column flex-grow">
                    {showTreeOverlay && <div className={"wiki-tree-reorder-overlay absolute-fill"} />}
                    <FilterBar filter={this._filter} className={"wiki-tree-filterbar flex-center"} ref={this._filterBarRef} searchRoleAriaLabel={Resources.SearchAriaLabel}>
                        <KeywordFilterBarItem placeholder={Resources.FilterPagePlaceholder} filterItemKey={this._filterKey} ariaLabel={Resources.FilterTreeWatermarkText}/>
                    </FilterBar>
                    {this.props.isFilterLoading ? <Spinner /> : this._renderTree()}
                </div>
            </div>
        );
    }

    private _onTreeFilterChanged = (changedFilter: IFilterState): void => {
        // calculate filtertext here
        if (changedFilter.hasOwnProperty(this._filterKey)) {
            const filterState = changedFilter[this._filterKey];
            const filterText: string = filterState ? filterState.value : "";

            if (filterText !== "") {
                this.props.onFilterChanged(filterText);
            } else {
                this.props.onFilteringComplete();
            }
        }
    };

    private _renderTree(): JSX.Element {
        const { isFiltering, filterTreeItemProvider } = this.props;
        const commonProps = {
            showLines: false,
            additionalColumns: this.props.additionalColumns || [],
            isIncludeEditingAllowed: this.props.isIncludeEditingAllowed,
            getPageIsDisabled: this.props.getPageIsDisabled
                ? this.props.getPageIsDisabled
                : (): boolean => {
                      return false;
                  }
        };
        let dragDropProps: WikiTreeDragDropProps | undefined;
        if (this.props.wikiTreeDragDropProps) {
            dragDropProps = {
                onDrop: this._onDrop,
                onDrag: this.props.wikiTreeDragDropProps.onDrag,
                canDrop: this.props.wikiTreeDragDropProps.canDrop,
                canDrag: this.props.wikiTreeDragDropProps.canDrag
            };
        }

        return (
            <>
                {isFiltering ? (
                    <WikiPagePathTree
                        {...this.props}
                        itemProvider={filterTreeItemProvider}
                        onToggle={(e, i) => filterTreeItemProvider.toggle(i.underlyingItem)}
                        onPathChanged={this.props.onFilteredPageSelected}
                        emptyTreeMsg={Resources.FilteredTree_NoResultsFound}
                        {...commonProps}
                    />
                ) : (
                    <WikiPagePathTree
                        {...this.props}
                        onToggle={this._onToggle}
                        onPathChanged={this.props.onPageSelected}
                        emptyTreeMsg={""}
                        dragDropProps={dragDropProps}
                        {...commonProps}
                    />
                )}
            </>
        );
    }

    private _onDrop = (target: WikiPage, isReorderOperation?: boolean, isReorderAbove?: boolean): void => {
        this.props.wikiTreeDragDropProps &&
            this.props.wikiTreeDragDropProps.onDrop(target, this.props.isHandleBrokenLinksEnabled, isReorderOperation, isReorderAbove);
    };

    private _onToggle = (event: React.SyntheticEvent<HTMLElement>, treeItem: ITreeItemEx<WikiTreePage>): void => {
        this.props.onPageToggled(treeItem.underlyingItem.data, !treeItem.underlyingItem.expanded);
    };
}
